import { createContext, useContext, type ReactNode } from "react";

export interface WindowActiveContextValue {
  /** Window is in the currently visible workspace. */
  isInActiveWorkspace: boolean;
  /**
   * Window should run live queries and WS handlers: active workspace,
   * not minimized, and topmost (focused) in that workspace.
   */
  isWindowLive: boolean;
}

const defaultValue: WindowActiveContextValue = {
  isInActiveWorkspace: true,
  isWindowLive: true,
};

const WindowActiveContext = createContext<WindowActiveContextValue>(defaultValue);

/** Supplies ``isWindowLive`` so child apps pause queries and WS when unfocused or minimised. */
export function WindowActiveProvider({
  value,
  children,
}: {
  value: WindowActiveContextValue;
  children: ReactNode;
}) {
  return (
    <WindowActiveContext.Provider value={value}>
      {children}
    </WindowActiveContext.Provider>
  );
}

/** Whether this OS window should fetch data and subscribe to agentic WS events. */
export function useWindowLive(): boolean {
  return useContext(WindowActiveContext).isWindowLive;
}

/** Workspace visibility and focus flags for an OS window (see ``useWindowLive`` for the fetch gate). */
export function useWindowActive(): WindowActiveContextValue {
  return useContext(WindowActiveContext);
}
